import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  reportItemOrUser,
  getReportedItemsForAdmin,
  getReviewDetails,
  listReviews,
} from "../../services/reportService";
import { getReportedItemDetailsAPI } from "../../services/adminService";
import {
  ReportedItem,
  ReportedItemDetailsData,
  ReportItemOrUserPayload,
  ReviewData,
  ListReviewsRequest,
} from "../../types";
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from "../../utils/constants";

interface ReportState {
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
  reportedItems?: ReportedItem[];
  reportedItemDetails?: ReportedItemDetailsData | null;
  reportedItemDetailsLoading?: boolean;
  reviewDetails?: ReviewData;
  reviewsList?: ReviewData[];
  reviewsListPagination?: {
    nextPageKey: string;
    itemsPerPage: number;
  };
}

const initialState: ReportState = {
  loading: false,
  error: null,
  success: false,
  message: null,
  reportedItems: [],
  reportedItemDetails: null,
  reportedItemDetailsLoading: false,
  reviewDetails: undefined,
  reviewsList: undefined,
  reviewsListPagination: undefined,
};

// Report item or user(s) - can handle multiple reports
export const reportItemOrUserAction = createAsyncThunk(
  "report/reportItemOrUser",
  async (
    payload: ReportItemOrUserPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await reportItemOrUser(
        payload.data,
        payload.reportType,
        payload.reportedBy
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

// Fetch reported items for admin
export const getReportedItemsForAdminAction = createAsyncThunk(
  "report/getReportedItemsForAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getReportedItemsForAdmin();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

// Fetch reported item details by reviewId (admin)
export const getReportedItemDetailsAction = createAsyncThunk(
  "report/getReportedItemDetails",
  async (reviewId: string, { rejectWithValue }) => {
    try {
      const response = await getReportedItemDetailsAPI(reviewId);
      return response as ReportedItemDetailsData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

// Fetch review details by reviewId
export const getReviewDetailsAction = createAsyncThunk(
  "report/getReviewDetails",
  async (reviewId: string, { rejectWithValue }) => {
    try {
      const response = await getReviewDetails(reviewId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

// List reviews
export const listReviewsAction = createAsyncThunk(
  "report/listReviews",
  async (request: ListReviewsRequest, { rejectWithValue }) => {
    try {
      const response = await listReviews(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearReportState: (state) => {
      state.error = null;
      state.success = false;
      state.message = null;
    },
    setReportError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(reportItemOrUserAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(reportItemOrUserAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = action.payload?.message || SUCCESS_MESSAGES.REPORT_SUBMITTED_SUCCESSFULLY;
      })
      .addCase(reportItemOrUserAction.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
        state.message = null;
      })
      .addCase(getReportedItemsForAdminAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(getReportedItemsForAdminAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = (action.payload as any)?.message || SUCCESS_MESSAGES.REPORTED_ITEMS_FETCHED_SUCCESSFULLY;
        state.reportedItems = action.payload?.reviews || [];
      })
      .addCase(getReportedItemsForAdminAction.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
        state.reportedItems = [];
      })
      .addCase(getReportedItemDetailsAction.pending, (state) => {
        state.reportedItemDetailsLoading = true;
        state.error = null;
      })
      .addCase(getReportedItemDetailsAction.fulfilled, (state, action) => {
        state.reportedItemDetailsLoading = false;
        state.reportedItemDetails = action.payload;
      })
      .addCase(getReportedItemDetailsAction.rejected, (state, action) => {
        state.reportedItemDetailsLoading = false;
        state.error = action.payload as string;
        state.reportedItemDetails = null;
      })
      .addCase(getReviewDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(getReviewDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = action.payload?.message || SUCCESS_MESSAGES.REVIEW_DETAILS_FETCHED_SUCCESSFULLY;
        state.reviewDetails = action.payload?.data;
      })
      .addCase(getReviewDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
        state.reviewDetails = undefined;
      })
      .addCase(listReviewsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(listReviewsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = action.payload?.message || SUCCESS_MESSAGES.REVIEWS_LIST_FETCHED_SUCCESSFULLY;
        state.reviewsList = action.payload?.data?.reviews || [];
        state.reviewsListPagination = {
          nextPageKey: action.payload?.data?.nextPageKey || "",
          itemsPerPage: action.payload?.data?.itemsPerPage || 0,
        };
      })
      .addCase(listReviewsAction.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
        state.reviewsList = undefined;
        state.reviewsListPagination = undefined;
      })
  },
});

export const { clearReportState, setReportError } = reportSlice.actions;
export const reportSelector = (state: { report: ReportState }) => state.report;
export default reportSlice.reducer;
